import axiosRetry from 'axios-retry';
import { serverLogger } from '../libs/datadog-logger/loggers/serverLogger';

function classifyTerminalRefreshFailure(error) {
  const status = error?.response?.status;
  const oauthError = error?.response?.data?.error;
  const message = `${error?.message || ''} ${error?.response?.data?.error_description || ''}`.trim();

  if (/refresh token not found/i.test(message)) {
    return 'missing_refresh_token';
  }

  if (/invalid authentication token/i.test(message)) {
    return 'invalid_authentication_token';
  }

  if (status === 401) {
    return 'unauthorized';
  }

  if (status === 400 && /invalid_grant|invalid_request/i.test(`${oauthError || ''} ${message}`)) {
    return 'oauth_invalid_grant';
  }

  return 'unknown_terminal_refresh_error';
}

export function logTerminalRefreshFailure(error, { config, flow }) {
  serverLogger.warn('auth.refresh_terminal_failure', {
    flow,
    reason: classifyTerminalRefreshFailure(error),
    url: config?.url,
    method: config?.method,
    errorCode: error?.code,
    errorMessage: error?.message,
    httpStatus: error?.response?.status,
    oauthError: error?.response?.data?.error,
    oauthErrorDescription: error?.response?.data?.error_description,
    isRefreshError: !!error?.isRefreshError,
  });
}

/**
 * Determines whether an error should fast-fail due to offline state.
 * Logs the offline condition with the provided debug logger.
 *
 * @param {Error & { code?: string }} error
 * @param {{ debugError: import('debug').Debugger }} loggers
 * @returns {boolean}
 */
export function shouldFastFailOffline(error, { debugError }) {
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const isNetworkError = axiosRetry.isNetworkError(error);

  if (!(isOffline && isNetworkError)) return false;

  debugError('step 8: device offline detected, fast-failing');
  console.debug('[httpClient] Request failed: device is offline');
  return true;
}

/**
 * Determines whether the current error qualifies for 401 retry handling.
 *
 * @param {import('axios').AxiosError} error
 * @param {import('axios').AxiosRequestConfig} config
 * @param {{ authlessPaths: string[], apiOrigin: string, isAuthless: Function, isSameApi: Function }} context
 * @returns {boolean}
 */
export function shouldRetry401(error, config, { authlessPaths, apiOrigin, isAuthless, isSameApi }) {
  const status = error?.response?.status;
  if (status !== 401) return false;
  if (isAuthless(config, authlessPaths, apiOrigin)) return false;
  if (!isSameApi(config, apiOrigin)) return false;
  return true;
}

/**
 * Increments and logs the retry count for the provided request config.
 *
 * @param {import('axios').AxiosRequestConfig & { meta?: Record<string, unknown> }} config
 * @param {number} maxRetries
 * @param {{ debugAuth: import('debug').Debugger }} loggers
 * @returns {number} The updated retry count
 */
export function trackRetryAttempt(config, maxRetries, { debugAuth }) {
  config.meta = config.meta || {};
  config.meta.authRetryCount = (config.meta.authRetryCount || 0) + 1;

  debugAuth('step 5: 401 error - per-request retry logic', {
    url: config.url,
    retryCount: config.meta.authRetryCount,
    maxRetries,
  });

  return config.meta.authRetryCount;
}

/**
 * Handles the scenario where the retry count exceeds the maximum allowed attempts.
 *
 * @param {{ client: any, config: import('axios').AxiosRequestConfig & { meta?: Record<string, unknown> }, error: any, maxRetries: number, tokenManager: any, debugAuth: import('debug').Debugger, debugError: import('debug').Debugger }} params
 */
export function handleMaxRetriesExceeded({
  client,
  config,
  error,
  maxRetries,
  tokenManager,
  debugAuth,
  debugError,
}) {
  debugError('step 5: max auth retries exceeded', {
    url: config.url,
    retryCount: config.meta?.authRetryCount,
    maxRetries,
    isRefreshError: !!error.isRefreshError,
  });

  console.error(`[httpClient] Max auth retries (${maxRetries}) exceeded for ${config.url}`);

  serverLogger.warn('auth.request_retry', {
    url: config.url,
    attempt: config.meta?.authRetryCount,
    maxRetries,
    isRefreshError: !!error.isRefreshError,
    exceededMax: true,
  });

  if (error.isRefreshError) {
    debugAuth('step 5: refresh error - triggering logout');
    tokenManager.logout();
    client.abortAll();
  }
}

/**
 * Attempts to refresh authentication and retry the original request.
 *
 * @param {{ client: any, config: import('axios').AxiosRequestConfig & { meta?: Record<string, unknown> }, maxRetries: number, tokenManager: any, debugAuth: import('debug').Debugger, debugError: import('debug').Debugger }} params
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function performAuthRetry({
  client,
  config,
  maxRetries,
  tokenManager,
  debugAuth,
  debugError,
}) {
  try {
    debugAuth('step 5: attempting token refresh for retry %d/%d', config.meta.authRetryCount, maxRetries);
    await tokenManager.refreshSingleFlight();
    debugAuth('step 5: refresh successful, retrying request');
    console.debug(`[httpClient] Auth retry ${config.meta.authRetryCount}/${maxRetries} for ${config.url}`);

    serverLogger.info('auth.request_retry', {
      url: config.url,
      attempt: config.meta.authRetryCount,
      maxRetries,
      isRefreshError: false,
      exceededMax: false,
    });

    const { signal: _signal, __abortController, ...retryConfig } = config;

    return client.request(retryConfig);
  } catch (refreshErr) {
    debugError('step 5: refresh failed during 401 retry', {
      url: config.url,
      refreshError: refreshErr.message,
    });

    refreshErr.isRefreshError = true;

    if (tokenManager.isTerminalRefreshError(refreshErr)) {
      logTerminalRefreshFailure(refreshErr, {
        config,
        flow: 'reactive_401_retry',
      });
      debugError('step 5: terminal refresh error, logging out');
      tokenManager.logout();
      client.abortAll();
    }

    throw refreshErr;
  }
}
